import { Link } from '@tanstack/react-router';
import { ArrowRight, BookOpen, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { ProblemSequence } from '@/lib/hooks/useProblemSequence';

/**
 * What to do next, shown once a submission is accepted.
 *
 * Before this, an accepted verdict dead-ended: the result panel reported
 * runtime and memory and offered nothing, the comprehension dialog's two
 * buttons were "Skip for now" and "Done", and the editorial unlocked silently
 * so most students never learned it had. The student's only route onward was
 * the browser back button.
 *
 * Renders nothing outside a named set. On a cold `/problems/$slug` there is no
 * meaningful "next", and "next: some unrelated catalogue problem" is worse
 * than staying quiet.
 */
export function NextInSequence({
  seq,
  onReadEditorial,
  origin,
}: {
  seq: ProblemSequence;
  /** Switches the left panel to the Editorial tab, which has just unlocked. */
  onReadEditorial?: () => void;
  origin: { topicId?: string; batchId?: string };
}) {
  if (!seq.isScoped || seq.index < 0) return null;

  const total = seq.entries.length;
  // The current solve is not in the cached counts yet — the topic/practice
  // query has not refetched at the moment this renders — so count it here
  // rather than showing the student a number that is one behind their own
  // accepted submission.
  const done = Math.min(total, seq.entries[seq.index]?.solved ? seq.solvedCount : seq.solvedCount + 1);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-foreground">{seq.label}</p>
        <p className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {done} of {total} done
        </p>
      </div>

      <div
        className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-blue-100"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${seq.label}: ${done} of ${total} problems solved`}
      >
        <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        {seq.next ? (
          <Button asChild size="sm" className="h-9 gap-1.5">
            {/* Named, not a bare arrow: the student decides before they click. */}
            <Link to="/problems/$slug" params={{ slug: seq.next.slug }} search={origin}>
              <span className="max-w-[220px] truncate">Next: {seq.next.title}</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            That was the last one in this set
          </span>
        )}

        {onReadEditorial && (
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={onReadEditorial}>
            <BookOpen className="h-4 w-4" />
            Read the editorial
          </Button>
        )}

        {origin.topicId && origin.batchId && (
          <Button asChild variant="ghost" size="sm" className="h-9">
            <Link
              to="/courses/$batchId/topics/$topicId/practice"
              params={{ batchId: origin.batchId, topicId: origin.topicId }}
            >
              Back to topic
            </Link>
          </Button>
        )}
        {origin.batchId && !origin.topicId && (
          <Button asChild variant="ghost" size="sm" className="h-9">
            <Link to="/courses/$batchId/practice" params={{ batchId: origin.batchId }}>
              Back to practice
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
